
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useReferralTracking } from "@/components/referral/ReferralTrackingProvider";

export const useReferralCleanup = () => {
  const { clearReferralCode } = useReferralTracking();

  // Enhanced cleanup after successful conversion
  const cleanupAfterConversion = useMutation({
    mutationFn: async ({ userId, subscriptionType }: { userId: string; subscriptionType: string }) => {
      console.log('Starting referral cleanup for user:', userId);
      
      try {
        // Get the referral code from storage
        const storedCode = localStorage.getItem('referral_code');
        if (!storedCode) {
          console.log('No referral code found, skipping cleanup');
          return { success: true, skipped: true };
        }

        let referralCode: string;
        try {
          const parsed = JSON.parse(storedCode);
          referralCode = parsed.code || parsed;
        } catch {
          referralCode = storedCode;
        }

        if (!referralCode) {
          console.log('Invalid referral code format, skipping cleanup');
          return { success: true, skipped: true };
        }

        // Find the referral code record
        const { data: codeData, error: codeError } = await supabase
          .from('referral_codes')
          .select('id, user_id')
          .eq('code', referralCode)
          .eq('is_active', true)
          .maybeSingle();

        if (codeError) {
          console.log('Error querying referral code:', codeError);
          clearReferralCode();
          return { success: true, error: 'Database query failed' };
        }

        if (!codeData) {
          console.log('Referral code not found in database');
          clearReferralCode();
          return { success: true, codeNotFound: true };
        }

        // Calculate commission based on subscription type
        const commissionMap: Record<string, number> = {
          'monthly': 5.00,
          'starter': 5.00,
          'team': 20.00,
          'pro': 20.00,
          'enterprise': 80.00,
          'growth': 80.00,
          'lifetime': 175.00
        };

        const commissionAmount = commissionMap[subscriptionType.toLowerCase()] || 0;

        if (commissionAmount === 0) {
          console.log('No commission for subscription type:', subscriptionType);
          clearReferralCode();
          return { success: true, noCommission: true };
        }

        // Create referral conversion record
        const { data: conversion, error: conversionError } = await supabase
          .from('referral_conversions')
          .insert({
            referral_code_id: codeData.id,
            referred_user_id: userId,
            subscription_type: subscriptionType,
            commission_amount: commissionAmount,
            status: 'confirmed'
          })
          .select()
          .maybeSingle();

        if (conversionError) {
          console.error('Error creating referral conversion:', conversionError);
          // Don't throw here, just log and continue
          clearReferralCode();
          return { success: true, error: 'Failed to create conversion record' };
        }

        console.log('Referral conversion created:', conversion);

        // Update referral analytics (optional, don't fail if this errors)
        try {
          const currentMonth = new Date();
          currentMonth.setDate(1); // First day of month
          
          await supabase.rpc('update_referral_analytics', {
            p_user_id: codeData.user_id,
            p_month_year: currentMonth.toISOString().split('T')[0]
          });
          
          console.log('Referral analytics updated');
        } catch (analyticsError) {
          console.warn('Error updating referral analytics (non-critical):', analyticsError);
        }

        // Clear the referral code from storage
        clearReferralCode();
        
        console.log('Referral cleanup completed successfully');
        return { 
          success: true, 
          conversion, 
          commission: commissionAmount,
          referrerId: codeData.user_id
        };

      } catch (error) {
        console.error('Error in referral cleanup:', error);
        // Clear code anyway to prevent issues
        clearReferralCode();
        // Don't throw the error, return success with error info
        return { 
          success: true, 
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    },
    onSuccess: (data) => {
      if (data.success && data.conversion) {
        console.log(`Referral conversion successful: $${data.commission} commission credited`);
      } else if (data.error) {
        console.log('Referral cleanup completed with issues:', data.error);
      }
    },
    onError: (error) => {
      console.error('Referral cleanup failed:', error);
      // Clear the code to prevent repeated failures
      clearReferralCode();
    }
  });

  // Cleanup expired referral codes from storage
  const cleanupExpiredCodes = useMutation({
    mutationFn: async () => {
      const stored = localStorage.getItem('referral_code');
      if (!stored) return { cleaned: false };

      try {
        // Handle legacy string format
        if (typeof stored === 'string' && !stored.startsWith('{')) {
          return { cleaned: false, legacy: true };
        }

        const referralData = JSON.parse(stored);
        if (referralData.expires && Date.now() > referralData.expires) {
          console.log('Cleaning up expired referral code');
          clearReferralCode();
          return { cleaned: true };
        }

        return { cleaned: false };
      } catch (error) {
        console.error('Error parsing referral code for cleanup:', error);
        clearReferralCode();
        return { cleaned: true, error: true };
      }
    }
  });

  return {
    cleanupAfterConversion,
    cleanupExpiredCodes
  };
};
